import {
  ExternalBankAccount,
  PayoutDestinationType,
  PreferredPayoutSpeed
} from "@wingspanhq/wingspan-api-sdk";
import {
  WSActions,
  WSCard,
  WSControl,
  WSControlGroupCards,
  WSEmptyState,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { useCallback, useState } from "react";
import { queriesInternalAccounts, queriesPayouts } from "@query";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { selectorPayoutSettingsInstantFee } from "@selectors/selectorPayoutSettingsInstantFee";
import {
  avatarBankAccount,
  avatarBankCard,
  avatarWallet
} from "../../../../shared/constants/avatars";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorPendingBankingAccount } from "../../../../shared/selectors/selectorPendingBankingAccount";

type UnavailableSelection = "debit-card" | "try-another-bank" | "wallet";

type Props = {
  failedAccount?: ExternalBankAccount;
  onBack: () => void;
  onNext?: () => void;
  onDebitCard: () => void;
  onTryAnotherBank: () => void;
  onActivateWallet: () => void;
};

export const InstantUnavailable: React.FC<Props> = props => {
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const payoutSettingsId = queryPayoutSettings.data?.id;
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();

  const hasActiveWallet = (queryInternalAccounts.data?.data || []).some(
    selectorActiveBankingAccount
  );
  const hasPendingWallet = (queryInternalAccounts.data?.data || []).some(
    selectorPendingBankingAccount
  );
  const fee = selectorPayoutSettingsInstantFee(queryPayoutSettings.data);

  const [selectedOption, setSelectedOption] = useState<
    UnavailableSelection | undefined
  >(undefined);
  const [useAsStandard, setUseAsStandard] = useState(false);

  const [updatePayoutSettings, updatePayoutSettingsMeta] =
    queriesPayouts.updatePayoutSettings.useMutation({
      onSuccess: props.onNext
    });

  const handleContinue = useCallback(() => {
    if (!payoutSettingsId) return;

    if (useAsStandard) {
      if (props.failedAccount) {
        updatePayoutSettings({
          payoutSettingsId,
          payoutSettingsUpdate: {
            preferredPayoutSpeed: PreferredPayoutSpeed.STANDARD,
            standardDestination: {
              id: props.failedAccount.id,
              type: PayoutDestinationType.EXTERNAL_BANK_ACCOUNT
            }
          }
        });
      }
      return;
    }

    if (selectedOption === "debit-card") {
      props.onDebitCard();
    } else if (selectedOption === "try-another-bank") {
      props.onTryAnotherBank();
    } else if (selectedOption === "wallet") {
      if (hasActiveWallet) {
        const activeWallet = (queryInternalAccounts.data?.data || []).find(
          selectorActiveBankingAccount
        );
        if (activeWallet) {
          updatePayoutSettings({
            payoutSettingsId,
            payoutSettingsUpdate: {
              preferredPayoutSpeed: PreferredPayoutSpeed.INSTANT,
              instantDestination: {
                id: activeWallet.id,
                type: PayoutDestinationType.INTERNAL_ACCOUNT
              }
            }
          });
        }
      } else {
        props.onActivateWallet();
      }
    }
  }, [
    payoutSettingsId,
    useAsStandard,
    selectedOption,
    hasActiveWallet,
    queryInternalAccounts.data,
    updatePayoutSettings,
    props
  ]);

  const isSelectionMade = useAsStandard || selectedOption !== undefined;
  const isLoading = updatePayoutSettingsMeta.isLoading;

  return (
    <WSList gap="2XL">
      <WSEmptyState
        type="search"
        title="Instant payout unavailable"
        description="This account isn't eligible for instant payouts. Please select a different account or choose a standard payout method."
        bgColor="white"
        orientation="horizontal"
        hasBorder
      />

      <WSCard
        header={{
          label: {
            text: "Try another method"
          }
        }}
      >
        <WSList gap="2XL">
          <WSText.ParagraphSm color="gray500">
            Connect a debit card, use your Wingspan Wallet, try another bank, or
            switch to standard payout.
          </WSText.ParagraphSm>

          <WSControlGroupCards
            value={selectedOption ?? ""}
            onChange={newValue => {
              setSelectedOption(newValue as UnavailableSelection);
              setUseAsStandard(false);
            }}
            options={[
              {
                header: {
                  label: {
                    text: `Connect a debit card (${fee}% fee)`,
                    helperText:
                      "Receive payments by connecting an eligible Visa or Mastercard debit card",
                    avatar: avatarBankCard
                  }
                },
                value: "debit-card"
              },
              {
                header: {
                  label: {
                    text: hasActiveWallet
                      ? `Wingspan Wallet (${fee}% fee)`
                      : `Activate Wingspan Wallet (${fee}% fee)`,
                    helperText: hasActiveWallet
                      ? "Use your Wingspan Wallet as your instant payout method"
                      : "Business bank account with instant access to your funds",
                    avatar: avatarWallet
                  },
                  ...(hasPendingWallet
                    ? {
                        value: {
                          pill: {
                            theme: "blue" as const,
                            text: "Pending activation",
                            badge: true,
                            type: "noBackground" as const
                          },
                          stacked: true
                        }
                      }
                    : {})
                },
                disabled: hasPendingWallet,
                value: "wallet"
              },
              {
                header: {
                  label: {
                    text: "Try another bank",
                    helperText:
                      "Try instant payout by connecting an account from another bank",
                    avatar: avatarBankAccount
                  }
                },
                value: "try-another-bank"
              }
            ]}
          />

          <WSInfoBox theme="info">
            <WSText.ParagraphXs mb="L" color="gray500">
              If you wish to use the bank account you provided as a{" "}
              <b>standard payout method</b> instead, check the box below.
            </WSText.ParagraphXs>

            <WSControl
              type="checkbox"
              value={useAsStandard}
              onChange={checked => {
                setUseAsStandard(checked);
                if (checked) {
                  setSelectedOption(undefined);
                }
              }}
              label="Add this bank account and receive you funds in the standard 2-5 business days."
              size="S"
            />
          </WSInfoBox>
        </WSList>
      </WSCard>

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Back",
            kind: "Tertiary",
            onClick: props.onBack
          },
          {
            label: "Continue",
            disabled: !isSelectionMade,
            loading: isLoading,
            onClick: handleContinue
          }
        ]}
      />
    </WSList>
  );
};
